import { MaskState } from "@/api/masks-types";
import { SubmissionState } from "@/api/submission-types";
import { ApiErrorAlert } from "@/components/ui/ApiErrorAlert";
import { EpisodeBreadcrumbs } from "@/components/ui/breadcrumbs/EpisodeBreadcrumbs";
import { MaskBreadcrumbs } from "@/components/ui/breadcrumbs/MaskBreadcrumbs";
import { TimestampBreadcrumbs } from "@/components/ui/breadcrumbs/TimestampBreadcrumbs";
import { Link } from "@/components/ui/Link";
import { BoxLoader } from "@/components/ui/loaders";
import { MaskStateChip } from "@/components/ui/state-chips/MaskStateChip";
import { SubmissionStateChip } from "@/components/ui/state-chips/SubmissionStateChip";
import { TagsRow } from "@/components/ui/tags/TagsRow";
import { useUser } from "@/queries/auth";
import { useMaskById } from "@/queries/masks/use-mask-by-id";
import { Box, Button, darken, List, Stack, Typography } from "@mui/material";
import { green, red } from "@mui/material/colors";
import { AssetGenerationMetadata } from "./AssetGenerationMetadata";
import styles from "./MaskDetails.module.css";

type Props = {
  backButton?: boolean;
  id: string;
  mode: "browse" | "validate";
  onModeChange?: (mode: "browse" | "validate") => void;
  onRejectWholeEpisode?: () => void;
};

export function MaskDetails({
  backButton,
  id,
  mode,
  onModeChange,
  onRejectWholeEpisode,
}: Props) {
  const { data, isLoading, error } = useMaskById(id);

  const { data: user } = useUser();

  function renderData() {
    if (!data) {
      return null;
    }
    const {
      episode,
      episodeTitle,
      rejectionReasons,
      state,
      submissionState,
      url,
      image,
    } = data;

    return (
      <>
        <EpisodeBreadcrumbs
          id={episode.id}
          category={episode.category}
          gender={episode.gender}
          story={episode.story}
          subcategory={episode.subcategory}
          backToEpisodePageTab={backButton ? "masks" : undefined}
          linkToEpisodePage={!backButton}
        />
        <Typography component={"h1"} variant={"h5"}>
          {episodeTitle}
        </Typography>
        <TagsRow episodeId={episode.id} />
        <MaskBreadcrumbs id={id} />
        <TimestampBreadcrumbs {...data} />
        {mode === "browse" && <AssetGenerationMetadata {...data} />}
        <Link to={`/episodes/${episode.id}`} state={{ tab: "masks" }}>
          Story summary
        </Link>
        {mode === "browse" && (
          <>
            <Stack direction={"row"} gap={1}>
              <SubmissionStateChip size={"medium"} state={submissionState} />
              <MaskStateChip size={"medium"} state={state} />
              {submissionState === SubmissionState.unsubmitted &&
                [MaskState.accepted, MaskState.rejected].includes(state) &&
                user?.id === episode.visualsAssignee?.id &&
                episode.submissionState === SubmissionState.unsubmitted && (
                  <Button
                    variant="outlined"
                    onClick={() => onModeChange && onModeChange("validate")}
                    sx={{ height: 32 }}
                  >
                    {"Validate again"}
                  </Button>
                )}
              {onRejectWholeEpisode && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onRejectWholeEpisode}
                  sx={{ height: 32 }}
                >
                  {"Reject whole story"}
                </Button>
              )}
            </Stack>
            {state === MaskState.rejected && !!rejectionReasons?.length && (
              <Box>
                <Typography variant={"body1"} color={"textSecondary"}>
                  Rejection reasons:
                </Typography>
                <List sx={{ listStyle: "initial", pl: 2, pb: 0 }}>
                  {rejectionReasons.map((reason) => (
                    <li key={reason}>
                      <Typography color={"textSecondary"}>{reason}</Typography>
                    </li>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
        {mode === "validate" && onModeChange && (
          <Stack direction={"row"} gap={1}>
            <Button
              variant="outlined"
              onClick={() => onModeChange("browse")}
              sx={{ height: 32 }}
            >
              {"Back to browse"}
            </Button>
          </Stack>
        )}
        <Stack
          direction={"row"}
          sx={[
            {
              m: "auto",
              paddingTop: 2,
              paddingBottom: 2,
              borderRadius: (theme) => theme.shape.borderRadius,
              backgroundColor:
                mode === "browse"
                  ? state === MaskState.accepted
                    ? green[50]
                    : state === MaskState.rejected
                      ? red[50]
                      : "background.paper"
                  : "background.paper",
            },
            (theme) =>
              theme.applyStyles("dark", {
                backgroundColor: "background.paper",
                border: "1px solid",
                borderColor:
                  mode === "browse"
                    ? state === MaskState.accepted
                      ? darken(green[900], 0.2)
                      : state === MaskState.rejected
                        ? darken(red[900], 0.2)
                        : "background.paper"
                    : "background.paper",
              }),
          ]}
          justifyContent={"space-between"}
        >
          <img
            src={image.url}
            style={{ width: "calc(50% - 4px)" }}
            className={styles.imageLeft}
          />
          <img
            src={url}
            style={{ width: "calc(50% - 4px)" }}
            className={styles.imageRight}
          />
        </Stack>
      </>
    );
  }

  return (
    <Stack gap={1.5}>
      <ApiErrorAlert error={error} />
      <BoxLoader isLoading={isLoading} height={100} />
      {renderData()}
    </Stack>
  );
}
